import {
  Box,
  Button,
  ClickAwayListener,
  InputLabel,
  MenuItem,
  Popper,
  TextField,
  Typography,
} from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { useEffect, useState } from "react";
import { ReactComponent as EditIcon } from "../../../../../assets/AccountingModule/fluent_edit.svg";
import { CustomizedRadio } from "../../../../../components/CustomRadio";
import Modal from "../../../../../components/Modal";
import color from "../../../../../constants/color";
import { useAlert } from "../../../../../context/AlertContext";
import AllTransactionList from "./AllTransactionList";
import style from "./style";
dayjs.extend(utc);

const InfoRow = ({ label, value, colorValue }) => (
  <Box sx={{ display: "flex", justifyContent: "space-between" }}>
    <Box>{label}</Box>
    <Box sx={{ color: colorValue }}>{value}</Box>
  </Box>
);

function AccountReconciliationPage({
  accountID,
  bankName,
  transactionData,
  vendors,
  customers,
  startDate,
  endDate,
  closingBalance,
  setClosingBalance,
  handleAddTransaction,
  handleReconcileTransactions,
  lastClosingBalance,
}) {
  const [clearedAmount, setClearedAmount] = useState(0);
  const [difference, setDifference] = useState(0);
  const [editBalanceValue, setEditBalanceValue] = useState(closingBalance ?? 0);
  const [anchorEl, setAnchorEl] = useState(null);
  const [totalSelectedDeposits, setTotalSelectedDeposits] = useState(0);
  const [totalSelectedWithdrawals, setTotalSelectedWithdrawals] = useState(0);
  const [openAddTransactionModal, setOpenAddTransactionModal] = useState(false);
  const [date, setDate] = useState(dayjs.utc(dayjs()).endOf("day"));
  const [transactionType, setTransactionType] = useState("Payments/Refunds");
  const [account, setAccount] = useState("");
  const [amount, setAmount] = useState("");
  const [reference, setReference] = useState("");
  const [description, setDescription] = useState("");
  const [selected, setSelected] = useState([]);
  const { showAlert } = useAlert();

  useEffect(() => {
    const cleared = totalSelectedDeposits - totalSelectedWithdrawals;
    setClearedAmount(cleared);
    setDifference(lastClosingBalance + cleared - closingBalance);
  }, [totalSelectedDeposits, totalSelectedWithdrawals, closingBalance]);

  useEffect(() => {
    if (openAddTransactionModal) {
      setTransactionType("Payments/Refunds");
      setAccount("");
      setAmount("");
      setReference("");
      setDate(dayjs.utc(dayjs()).endOf("day"));
      setDescription("");
    }
  }, [openAddTransactionModal]);

  useEffect(() => {
    setEditBalanceValue(closingBalance);
  }, [closingBalance]);

  const handleEditClick = (event) => {
    setEditBalanceValue(closingBalance);
    setAnchorEl(event.currentTarget);
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
  };

  const handleUpdateBalance = () => {
    setClosingBalance(Number(editBalanceValue));
    handlePopoverClose();
  };

  const open = Boolean(anchorEl);
  const handleModalClose = () => {
    setOpenAddTransactionModal(false);
  };

  const handleAddTransactionSubmit = async () => {
    if (amount <= 0) {
      showAlert("Amount must be greater than 0", "warning");
      return;
    }
    const formData = {
      transactionType,
      date: date.toISOString(),
      reference,
      description,
      account: accountID,
      status: "Added Manually",
    };

    if (transactionType === "Payments/Refunds") {
      formData.customer = account;
      formData.deposit = amount;
      formData.withdrawal = "";
    } else if (transactionType === "Charges") {
      formData.vendor = account;
      formData.deposit = "";
      formData.withdrawal = amount;
    }

    const response = await handleAddTransaction(formData);
    if (response.success) {
      handleModalClose();
    } else {
      showAlert("Transaction failed to add", "error");
    }
  };

  const handleReconcileTransactionsSubmit = async (status, draft) => {
    if(difference !== 0) {
      showAlert("Clear all amounts until the difference is zero to reconcile.", "warning");
      return;
    }
    const formData = {
      account: accountID,
      startDate: startDate,
      endDate: endDate,
      reconciledTransactionIds: selected,
      status
    };
    await handleReconcileTransactions(formData, draft);
  }

  return (
    <>
      {/* Header */}
      <Box
        px={2}
        pt={2.5}
        display="flex"
        justifyContent="space-between"
        alignItems="center"
      >
        <Typography variant="h5" sx={style.heading}>
          {bankName}
        </Typography>
        <Button
          variant="contained"
          color="success"
          sx={style.importStatementBtn}
          onClick={() => setOpenAddTransactionModal(true)}
        >
          Add Transaction
        </Button>
      </Box>

      {/* Transaction List */}
      <Box sx={{ backgroundColor: color.card, m: 2, borderRadius: "20px" }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            p: 2,
          }}
        >
          <Typography sx={{ fontWeight: 600 }}>
            <Box component="span" sx={{ color: color.primary }}>
              Period
            </Box>{" "}
            : {dayjs.utc(startDate).format("YYYY-MM-DD")} to{" "}
            {dayjs.utc(endDate).format("YYYY-MM-DD")}
          </Typography>
        </Box>

        <AllTransactionList
          data={transactionData}
          vendors={vendors}
          customers={customers}
          startDate={startDate}
          setTotalSelectedDeposits={setTotalSelectedDeposits}
          setTotalSelectedWithdrawals={setTotalSelectedWithdrawals}
          selected={selected}
          setSelected={setSelected}
          lastClosingBalance={lastClosingBalance}
        />
      </Box>

      {/* Reconciliation Summary */}
      <Box sx={{ display: "flex", justifyContent: "end" }}>
        <Box
          sx={{
            backgroundColor: color.card,
            m: 2,
            borderRadius: "20px",
            width: "400px",
            p: 2,
            display: "flex",
            flexDirection: "column",
            gap: 1.5,
            fontWeight: 500,
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Box sx={{ display: "flex", flexDirection: "column" }}>
              <Typography sx={{ fontWeight: 500 }}>Closing Balance</Typography>
              <Typography variant="caption" sx={{ color: color.lightText }}>
                As of {dayjs.utc(endDate).format("YYYY-MM-DD")}
              </Typography>
            </Box>
            <Box
              onClick={handleEditClick}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                cursor: "pointer",
              }}
            >
              <Typography sx={{ fontWeight: 500 }}>
                {typeof closingBalance === "number"
                  ? `$${closingBalance.toLocaleString()}`
                  : "$0"}
              </Typography>
              <EditIcon />
            </Box>
          </Box>

          <InfoRow
            label="Cleared Amount"
            value={`$${clearedAmount.toLocaleString()}`}
            colorValue={color.primary}
          />
          <InfoRow
            label="Difference"
            value={`$${difference.toLocaleString()}`}
            colorValue={color.red}
          />
        </Box>
      </Box>

      {/* Tooltip-style Popper ABOVE with arrow pointing down */}
      <Popper
        open={open}
        anchorEl={anchorEl}
        placement="top-end"
        sx={{ zIndex: 1300 }}
      >
        <ClickAwayListener onClickAway={handlePopoverClose}>
          <Box
            sx={{
              mb: 1.5,
              backgroundColor: "#fff",
              border: "1px solid #ccc",
              borderRadius: "20px",
              px: 3,
              py: 2,
              minWidth: 220,
              maxWidth: 250,
              boxShadow: 1,
              position: "relative",
              "&::before": {
                content: '""',
                position: "absolute",
                bottom: "-12px", // move a little lower than after
                left: "calc(83% - 9px)",
                width: 0,
                height: 0,
                borderLeft: "10px solid transparent",
                borderRight: "10px solid transparent",
                borderTop: "12px solid #ccc", // border layer
                zIndex: 0,
              },
              "&::after": {
                content: '""',
                position: "absolute",
                bottom: "-11px",
                left: "calc(83% - 8px)",
                width: 0,
                height: 0,
                borderLeft: "9px solid transparent",
                borderRight: "9px solid transparent",
                borderTop: "11px solid #fff", // inner fill
                zIndex: 1,
              },
            }}
          >
            <Typography
              sx={{
                fontSize: "16px",
                fontWeight: "400",
                color: color.text,
                mt: 1,
              }}
            >
              Closing Balance
            </Typography>
            <TextField
              fullWidth
              value={editBalanceValue}
              onChange={(e) =>
                setEditBalanceValue(e.target.value.replace(/[^0-9.-]/g, ""))
              }
              InputProps={{
                sx: {
                  borderRadius: "8px",
                  my: 1,
                  fontSize: "14px",
                  fontWeight: "400",
                },
              }}
            />
            <Button
              variant="contained"
              sx={{
                mt: 2,
                fontSize: 14,
                borderRadius: "20px",
                backgroundColor: color.primary,
              }}
              fullWidth
              onClick={handleUpdateBalance}
            >
              Update
            </Button>
          </Box>
        </ClickAwayListener>
      </Popper>

      {/* Action Buttons */}
      <Box sx={{ backgroundColor: color.card, m: 2, borderRadius: "20px" }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "end",
            alignItems: "center",
            p: 2,
            gap: 2,
          }}
        >
          <Button
            variant="contained"
            color="success"
            sx={{ ...style.importStatementBtn, px: 3 }}
            onClick={() => {handleReconcileTransactionsSubmit("in-progress", true)}}
          >
            Save and Reconcile Later
          </Button>
          <Button
            variant="contained"
            sx={{
              ...style.importStatementBtn,
              backgroundColor: color.primary,
              px: 3,
            }}
            onClick={() => {handleReconcileTransactionsSubmit("reconciled", false)}}
          >
            Reconcile
          </Button>
          <Button
            variant="contained"
            sx={{
              ...style.importStatementBtn,
              backgroundColor: color.lightText,
              px: 3,
            }}
            onClick={() => {}}
          >
            Cancel
          </Button>
        </Box>
      </Box>

      <Modal
        open={openAddTransactionModal}
        setOpen={handleModalClose}
        title="Add Transaction"
        btnText="Save"
        cancelText="Cancel"
        handleCheck={handleAddTransactionSubmit}
        disabled={!account || !amount || !reference || !description}
      >
        <Box sx={style.modalGrid}>
          <Box sx={style.boxs2}>
            <InputLabel sx={style.label}>Apply To</InputLabel>
            <Box sx={{ display: "flex", gap: 2 }}>
              <CustomizedRadio
                handleChange={setTransactionType}
                value={transactionType}
                defaultChecked={transactionType}
                radioLabel={[
                  { label: "Payments/Refunds", value: "Payments/Refunds" },
                  { label: "Charges", value: "Charges" },
                ]}
              />
            </Box>
          </Box>
        </Box>

        <Box sx={style.modalGrid}>
          <Box sx={{ width: "50%", ml: "5px" }}>
            <InputLabel sx={style.label}>Date</InputLabel>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <Box>
                <DatePicker
                  value={date}
                  slotProps={{
                    field: {
                      readOnly: true,
                    },
                  }}
                  sx={style.calenderInput}
                  onChange={(newValue) => {
                    const normalizedEnd = dayjs.utc(newValue).endOf("day");
                    setDate(normalizedEnd);
                  }}
                />
              </Box>
            </LocalizationProvider>
          </Box>
          <Box sx={{ width: "50%", ml: "5px" }}>
            <InputLabel sx={style.label}>Account</InputLabel>
            <TextField
              name="account"
              select
              value={account}
              type="text"
              onChange={(e) => setAccount(e.target.value)}
              required
              sx={style.boxs2}
            >
              {(transactionType === "Payments/Refunds"
                ? customers
                : vendors
              )?.map((item) => (
                <MenuItem key={item._id} value={item._id}>
                  {item.name}
                </MenuItem>
              ))}
            </TextField>
          </Box>
        </Box>

        <Box sx={style.modalGrid}>
          <Box sx={{ width: "50%", ml: "5px" }}>
            <InputLabel sx={style.label}>Amount</InputLabel>
            <TextField
              type="number"
              name="amount"
              value={amount}
              onChange={(e) =>
                setAmount(e.target.value.replace(/[^0-9.]/g, ""))
              }
              sx={style.boxs2}
            />
          </Box>
          <Box sx={{ width: "50%", ml: "5px" }}>
            <InputLabel sx={style.label}>
              {transactionType === "Payments/Refunds"
                ? "Reference#"
                : "Invoice#"}
            </InputLabel>
            <TextField
              type="text"
              name="reference"
              value={reference}
              onChange={(e) => setReference(e.target.value)}
              sx={style.boxs2}
            />
          </Box>
        </Box>

        <Box sx={style.modalGrid}>
          <Box sx={{ width: "100%", ml: "5px" }}>
            <InputLabel sx={style.label}>Description</InputLabel>
            <TextField
              name="description"
              value={description}
              onChange={(e) => {
                setDescription(e.target.value);
              }}
              sx={style.boxs2}
              multiline
              rows={2}
            />
          </Box>
        </Box>
      </Modal>
    </>
  );
}

export default AccountReconciliationPage;
